import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useEmployees } from '@/hooks/use-employees';
import {
  useAddProjectMembers,
  useProjectMembers,
  useRemoveProjectMember,
  useUpdateProjectMember,
} from '@/hooks/use-projects';
import { formatDateTime } from '@/lib/date';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { usePermission } from '@/lib/permissions';
import { useNow } from '@/hooks/use-now';
import { liveWindow, openUntilLabel } from '@/features/time-tracker/lib/reopen-window';
import { ReopenPeriodDialog } from './reopen-period-dialog';
import type { ProjectDetail, ProjectMemberItem } from '@hygge/shared';
import { AlarmClockPlus, Pencil, Search, UserMinus, UserPlus, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface MembersTabProps {
  project: ProjectDetail;
}

export function MembersTab({ project }: MembersTabProps) {
  const { data: members, isLoading } = useProjectMembers(project.id);
  const formatName = useEmployeeNameFormatter();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<ProjectMemberItem | null>(null);
  const [editTarget, setEditTarget] = useState<ProjectMemberItem | null>(null);
  const removeMutation = useRemoveProjectMember(project.id);
  // Super Admin by the hook's bypass; anyone else by the matrix grant. The
  // server asks the project's view rule too, and this tab is shown only where
  // it holds. [HW-963]
  const canReopen = usePermission('time.reopen.grant');
  const [reopenTarget, setReopenTarget] = useState<ProjectMemberItem | null>(null);
  const now = useNow(30_000);

  const activeMembers = useMemo(
    () => (members ?? []).filter((m) => !m.removedAt),
    [members],
  );

  const handleRemove = async () => {
    if (!removeTarget) return;
    await removeMutation.mutateAsync(removeTarget.employeeId);
    setRemoveTarget(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-border">
            <Skeleton className="h-9 w-9 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {activeMembers.length} member{activeMembers.length !== 1 ? 's' : ''}
        </p>
        <Button
          size="sm"
          className="gap-1.5 cursor-pointer"
          onClick={() => setIsAddOpen(true)}
        >
          <UserPlus className="h-3.5 w-3.5" />
          Add Members
        </Button>
      </div>

      {/* Members list */}
      {activeMembers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Users className="h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm font-medium text-foreground">No members yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add team members to this project</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Member</th>
                <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Department</th>
                <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Location</th>
                <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Joined</th>
                <th className="w-10 px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {activeMembers.map((member) => {
                const isManager = member.isManager;
                return (
                  <tr key={member.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.avatarUrl || undefined} />
                          <AvatarFallback className="text-[10px]">
                            {member.firstName[0]}{member.lastName[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-foreground">
                              {formatName({ firstName: member.firstName, lastName: member.lastName })}
                            </span>
                            {isManager && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 text-hygge border-hygge/30">
                                PM
                              </Badge>
                            )}
                          </div>
                          {member.role && (
                            <p className="text-xs text-muted-foreground">{member.role}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {member.departmentName || '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {member.location || '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {member.joinedAt
                        ? formatDateTime(member.joinedAt, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <TooltipProvider delayDuration={150}>
                        <div className="flex items-center justify-end gap-1">
                          {(() => {
                            const open = liveWindow(member.closedPeriod, now);
                            return open ? (
                              <span className="text-[11px] text-hygge whitespace-nowrap mr-1">
                                {openUntilLabel(open.openUntil, now)}
                              </span>
                            ) : null;
                          })()}
                          {/* A project manager is exempt from the monthly cutoff on
                              their project, and one who has left the company would not use
                              a window, so neither is offered one. [HW-963] */}
                          {canReopen && !isManager && member.isCurrentEmployee !== false && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground cursor-pointer"
                                  onClick={() => setReopenTarget(member)}
                                  aria-label="Reopen closed period"
                                >
                                  <AlarmClockPlus className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Reopen closed period</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground cursor-pointer"
                                onClick={() => setEditTarget(member)}
                                aria-label="Edit member"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit member</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded-full text-muted-foreground hover:text-destructive cursor-pointer"
                                onClick={() => setRemoveTarget(member)}
                                aria-label="Remove member"
                              >
                                <UserMinus className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove member</TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add members dialog */}
      <AddMembersDialog
        projectId={project.id}
        existingMemberIds={activeMembers.map((m) => m.employeeId)}
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
      />

      {/* Edit member dialog */}
      <EditMemberDialog
        projectId={project.id}
        member={editTarget}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />

      {/* Reopen a closed period for one member. [HW-963] */}
      <ReopenPeriodDialog
        projectId={project.id}
        member={reopenTarget}
        onOpenChange={(open) => !open && setReopenTarget(null)}
      />

      {/* Remove confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <strong>{removeTarget ? formatName({ firstName: removeTarget.firstName, lastName: removeTarget.lastName }) : ''}</strong> from this project? They can be re-added later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              variant="destructive"
              className="cursor-pointer"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddMembersDialog({
  projectId,
  existingMemberIds,
  open,
  onOpenChange,
}: {
  projectId: string;
  existingMemberIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const addMutation = useAddProjectMembers(projectId);

  const { data: employeesData } = useEmployees({
    limit: 100,
    search: search || undefined,
  });

  const availableEmployees = useMemo(() => {
    return (employeesData?.data ?? []).filter(
      (e) => !existingMemberIds.includes(e.id) && e.status !== 'terminated',
    );
  }, [employeesData, existingMemberIds]);

  const handleToggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleAdd = async () => {
    if (selected.length === 0) return;
    await addMutation.mutateAsync(selected);
    setSelected([]);
    setSearch('');
    onOpenChange(false);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setSelected([]);
      setSearch('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Members</DialogTitle>
          <DialogDescription>
            Select employees to add to this project
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search employees..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="max-h-64 overflow-y-auto -mx-1 px-1">
          {availableEmployees.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              {search ? 'No matching employees found' : 'All employees are already members'}
            </p>
          ) : (
            <div className="space-y-1">
              {availableEmployees.map((emp) => {
                const isSelected = selected.includes(emp.id);
                return (
                  <button
                    key={emp.id}
                    onClick={() => handleToggle(emp.id)}
                    className={`flex items-center gap-2.5 w-full p-2 rounded-md text-left transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-hygge/10 ring-1 ring-hygge/30'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={emp.avatarUrl || undefined} />
                      <AvatarFallback className="text-[9px]">
                        {emp.firstName[0]}{emp.lastName[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {formatName({ firstName: emp.firstName, lastName: emp.lastName })}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {emp.position?.title || 'No position'}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="h-4 w-4 rounded-full bg-cta flex items-center justify-center">
                        <svg className="h-2.5 w-2.5 text-cta-foreground"fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} className="cursor-pointer">
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={selected.length === 0 || addMutation.isPending}
            className="cursor-pointer"
          >
            {addMutation.isPending
              ? 'Adding...'
              : `Add ${selected.length > 0 ? `(${selected.length})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditMemberDialog({
  projectId,
  member,
  onOpenChange,
}: {
  projectId: string;
  member: ProjectMemberItem | null;
  onOpenChange: (open: boolean) => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const updateMutation = useUpdateProjectMember(projectId);
  const [isManager, setIsManager] = useState(false);
  const [joinedAt, setJoinedAt] = useState('');

  useEffect(() => {
    if (member) {
      setIsManager(member.isManager);
      setJoinedAt(member.joinedAt ? member.joinedAt.slice(0, 10) : '');
    }
  }, [member]);

  const handleSave = async () => {
    if (!member) return;
    await updateMutation.mutateAsync({
      employeeId: member.employeeId,
      isManager,
      joinedAt: joinedAt || undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={!!member} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Member</DialogTitle>
          <DialogDescription>
            {member ? formatName({ firstName: member.firstName, lastName: member.lastName }) : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <div>
              <Label htmlFor="is-manager-switch" className="text-sm font-medium">
                Project manager
              </Label>
              <p className="text-xs text-muted-foreground">
                Grants PM-scoped access to roster, financials, and project edits.
              </p>
            </div>
            <Switch
              id="is-manager-switch"
              checked={isManager}
              onCheckedChange={setIsManager}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="joined-at-input" className="text-sm font-medium">
              Joined
            </Label>
            <Input
              id="joined-at-input"
              type="date"
              value={joinedAt}
              onChange={(e) => setJoinedAt(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="cursor-pointer"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
